import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Check } from 'lucide-react';
import { listSkills } from '@/services/skillsApi';
import type { ManualSkill } from '@Types/skills';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

export interface SkillSlashMenuHandle {
  moveHighlight: (delta: number) => void;
  selectHighlighted: () => ManualSkill | null;
}

interface Props {
  anchorRef: React.RefObject<HTMLElement>;
  query: string;
  activeIds: Set<string>;
  onToggle: (skill: ManualSkill) => void;
  onClose: () => void;
}

const MENU_WIDTH = 480;
const VIEWPORT_MARGIN = 8;

export const SkillSlashMenu = forwardRef<SkillSlashMenuHandle, Props>(
  ({ anchorRef, query, activeIds, onToggle, onClose }, ref) => {
    const [skills, setSkills] = useState<ManualSkill[]>([]);
    const [loading, setLoading] = useState(true);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [position, setPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      let cancelled = false;
      listSkills()
        .then(res => {
          if (!cancelled) setSkills([...res.tenant, ...res.user]);
        })
        .catch(() => {
          if (!cancelled) setSkills([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, []);

    useLayoutEffect(() => {
      const place = () => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        const popoverHeight = containerRef.current?.offsetHeight ?? 240;
        const spaceBelow = window.innerHeight - rect.bottom;
        const placeBelow = spaceBelow >= popoverHeight + 8 + VIEWPORT_MARGIN;
        const top = placeBelow
          ? rect.bottom + 8
          : Math.max(VIEWPORT_MARGIN, rect.top - popoverHeight - 8);
        let left = rect.left;
        const maxLeft = window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN;
        if (left > maxLeft) left = maxLeft;
        if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
        setPosition({ top, left });
      };
      place();
      window.addEventListener('resize', place);
      window.addEventListener('scroll', place, true);
      return () => {
        window.removeEventListener('resize', place);
        window.removeEventListener('scroll', place, true);
      };
    }, [anchorRef, loading, skills.length]);

    const sorted = useMemo(
      () =>
        [...skills].sort((a, b) => {
          if (a.last_used_at && b.last_used_at)
            return (
              new Date(b.last_used_at).getTime() -
              new Date(a.last_used_at).getTime()
            );
          if (a.last_used_at) return -1;
          if (b.last_used_at) return 1;
          return 0;
        }),
      [skills],
    );

    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return sorted;
      return sorted.filter(s => s.name.toLowerCase().includes(q));
    }, [sorted, query]);

    useEffect(() => {
      setHighlightedIndex(0);
    }, [query, skills.length]);

    useImperativeHandle(
      ref,
      () => ({
        moveHighlight: (delta: number) => {
          if (filtered.length === 0) return;
          setHighlightedIndex(prev => {
            const next = (prev + delta + filtered.length) % filtered.length;
            return next;
          });
        },
        selectHighlighted: () => {
          if (filtered.length === 0) return null;
          const skill =
            filtered[Math.min(highlightedIndex, filtered.length - 1)];
          if (skill) {
            trackAdpWaypoint(
              getBernoulliAdp(),
              ADP_EVENTS.SKILLS_SLASH_MENU_SELECT,
              {
                skill_id: skill.skill_id,
                skill_name: skill.name,
                method: 'keyboard',
              },
            );
            onToggle(skill);
          }
          return skill ?? null;
        },
      }),
      [filtered, highlightedIndex, onToggle],
    );

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef.current?.contains(target)) return;
        if (anchorRef.current?.contains(target)) return;
        onClose();
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, anchorRef]);

    return createPortal(
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          top: position?.top ?? -9999,
          left: position?.left ?? -9999,
          width: MENU_WIDTH,
          visibility: position ? 'visible' : 'hidden',
        }}
        className="bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto max-h-64">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-dark border-t-transparent" />
            </div>
          ) : skills.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
              <Sparkles className="h-6 w-6 text-slate-300 mb-1" />
              <p className="text-xs text-[#252F33] font-medium">
                No skills yet
              </p>
              <p className="text-xs text-[#768D95] mt-0.5">
                Create a skill, or wait for a tenant admin to publish one.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-[#768D95]">
              No matches
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map((skill, idx) => {
                const active = activeIds.has(skill.skill_id);
                const highlighted = idx === highlightedIndex;
                return (
                  <li key={skill.skill_id}>
                    <button
                      type="button"
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onMouseDown={e => {
                        e.preventDefault();
                        trackAdpWaypoint(
                          getBernoulliAdp(),
                          ADP_EVENTS.SKILLS_SLASH_MENU_SELECT,
                          {
                            skill_id: skill.skill_id,
                            skill_name: skill.name,
                            method: 'click',
                          },
                        );
                        onToggle(skill);
                        onClose();
                      }}
                      className={`w-full flex items-start gap-2 px-3 py-2 text-left ${
                        highlighted
                          ? 'bg-[#E0E0E0]'
                          : active
                          ? 'bg-[#F5F5F5]'
                          : ''
                      }`}>
                      <div className="flex-shrink-0 w-5 h-5 mt-0.5 rounded border border-slate-300 flex items-center justify-center bg-white">
                        {active && (
                          <Check className="h-3.5 w-3.5 text-brand-dark" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold text-[#252F33] truncate">
                            {skill.name}
                          </p>
                          {skill.scope === 'tenant' && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Tenant
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#768D95] line-clamp-2">
                          {skill.description}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {!loading && filtered.length > 0 && (
          <div className="border-t border-slate-100 px-3 py-1.5 flex gap-3">
            <span className="text-[10px] text-slate-400">↑↓ navigate</span>
            <span className="text-[10px] text-slate-400">↵ select</span>
            <span className="text-[10px] text-slate-400">esc cancel</span>
          </div>
        )}
      </div>,
      document.body,
    );
  },
);

SkillSlashMenu.displayName = 'SkillSlashMenu';
